/**
 * List of Telegram users with edit/delete actions
 */
import { useState } from 'react';
import { Shield, User } from 'lucide-react';
import type { TelegramUser } from '@/shared/api/admin-users-api';
import { EditUserSheet } from './EditUserSheet';
import { haptic } from '@/shared/utils/telegram';

interface UsersListProps {
  users: TelegramUser[];
}

export function UsersList({ users }: UsersListProps) {
  const [selectedUser, setSelectedUser] = useState<TelegramUser | null>(null);
  const [isEditOpen, setIsEditOpen] = useState(false);

  const handleUserClick = (user: TelegramUser) => {
    haptic.impact('light');
    setSelectedUser(user);
    setIsEditOpen(true);
  };

  const handleCloseEdit = () => {
    setIsEditOpen(false);
    setTimeout(() => setSelectedUser(null), 300);
  };

  if (users.length === 0) {
    return (
      <div className="bg-[var(--tg-theme-secondary-bg-color)] rounded-2xl p-12 text-center border border-black/5 dark:border-white/5">
        <User size={48} className="mx-auto mb-3 text-[var(--tg-theme-hint-color)]" />
        <p className="text-sm font-medium text-[var(--tg-theme-hint-color)]">
          No users yet
        </p>
        <p className="text-xs text-[var(--tg-theme-hint-color)] mt-1">
          Add your first user to get started
        </p>
      </div>
    );
  }

  return (
    <>
      <div className="space-y-2">
        {users.map((user) => (
          <button
            key={user.id}
            onClick={() => handleUserClick(user)}
            className="w-full bg-[var(--tg-theme-secondary-bg-color)] rounded-xl p-4 text-left border border-black/5 dark:border-white/5 hover:bg-[var(--tg-theme-bg-color)] transition-colors"
          >
            <div className="flex items-start justify-between gap-3">
              {/* User Info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-sm font-black text-[var(--tg-theme-text-color)] font-mono">
                    {user.telegramUserId}
                  </span>
                  {user.role === 'admin' && (
                    <Shield size={14} className="text-emerald-500 flex-shrink-0" />
                  )}
                </div>

                {/* Username / Name */}
                {(user.username || user.firstName) && (
                  <div className="text-xs text-[var(--tg-theme-hint-color)] truncate">
                    {user.username && <span>@{user.username}</span>}
                    {user.username && (user.firstName || user.lastName) && (
                      <span className="mx-1">·</span>
                    )}
                    {user.firstName && (
                      <span>
                        {user.firstName} {user.lastName}
                      </span>
                    )}
                  </div>
                )}
              </div>

              {/* Badges */}
              <div className="flex items-center gap-2 flex-shrink-0">
                {/* Role Badge */}
                <span
                  className={`text-[10px] font-black uppercase px-2 py-1 rounded ${
                    user.role === 'admin'
                      ? 'bg-emerald-500/10 text-emerald-600'
                      : 'bg-blue-500/10 text-blue-600'
                  }`}
                >
                  {user.role}
                </span>

                {/* Status Badge */}
                <span
                  className={`text-[10px] font-black uppercase px-2 py-1 rounded ${
                    user.status === 'pending'
                      ? 'bg-amber-500/10 text-amber-600'
                      : user.status === 'rejected'
                        ? 'bg-rose-500/10 text-rose-600'
                        : user.isActive
                          ? 'bg-green-500/10 text-green-600'
                          : 'bg-gray-500/10 text-gray-600'
                  }`}
                >
                  {user.status === 'pending'
                    ? 'Pending'
                    : user.status === 'rejected'
                      ? 'Rejected'
                      : user.isActive
                        ? 'Active'
                        : 'Disabled'}
                </span>
              </div>
            </div>
          </button>
        ))}
      </div>

      {/* Edit User Sheet */}
      {selectedUser && (
        <EditUserSheet
          isOpen={isEditOpen}
          onClose={handleCloseEdit}
          user={selectedUser}
        />
      )}
    </>
  );
}
